import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { Alert, Badge, Button, Card, Field, Input, statusTone } from '../components/ui';
import { OrgNutrientMappings } from '../components/org/OrgNutrientMappings';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { ALL_ROLES, type MemberRow, type Role } from '../lib/types';

export function MembersPage() {
  const { t } = useTranslation();
  const { identity } = useSession();
  const orgId = identity?.org?.id;
  const isAdmin = identity?.roles.includes('admin') || identity?.superAdmin;
  const canEditNutrients = !!(isAdmin || identity?.roles.includes('ra'));
  const [activeTab, setActiveTab] = useState<'members' | 'nutrients'>('members');
  const [data, setData] = useState<{
    members: MemberRow[];
    seats: { max: number; used: number; available: number } | null;
    adminEmail: string;
  } | null>(null);
  const [msg, setMsg] = useState<{ tone: 'red' | 'green'; text: string } | null>(null);
  const [editing, setEditing] = useState<{ uid: string; roles: Role[] } | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState<{ email: string; roles: Role[] }>({ email: '', roles: ['ra'] });

  const load = useCallback(async () => {
    if (!orgId) return;
    setData(await api(`/api/v1/orgs/${orgId}/members`));
  }, [orgId]);
  useEffect(() => {
    void load();
  }, [load]);
  if (identity && identity.state !== 'member') return <Navigate to="/" replace />;

  const fail = (e: unknown) =>
    setMsg({
      tone: 'red',
      text:
        e instanceof ApiError
          ? t([`members.err_${e.code}`, 'members.err_generic'], { code: e.code })
          : String(e),
    });

  const saveRoles = async () => {
    if (!editing) return;
    try {
      await api(`/api/v1/orgs/${orgId}/members/${editing.uid}`, {
        method: 'PATCH',
        json: { roles: editing.roles },
      });
      setEditing(null);
      await load();
    } catch (e) {
      fail(e);
    }
  };
  const sendInvite = async () => {
    try {
      await api(`/api/v1/orgs/${orgId}/invites`, { method: 'POST', json: invite });
      setMsg({ tone: 'green', text: t('members.sent', { email: invite.email }) });
      setInviteOpen(false);
      setInvite({ email: '', roles: ['ra'] });
      await load();
    } catch (e) {
      fail(e);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{t('org.settingsTitle', 'Organization Settings')}</h1>
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1 text-sm">
          <button
            className={`rounded-md px-3 py-1.5 font-medium transition ${activeTab === 'members' ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
            onClick={() => setActiveTab('members')}
          >
            {t('members.title', 'Members & Seats')}
          </button>
          <button
            className={`rounded-md px-3 py-1.5 font-medium transition ${activeTab === 'nutrients' ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
            onClick={() => setActiveTab('nutrients')}
          >
            {t('nutrients.title', 'Nutrient Mappings')}
          </button>
        </div>
        {activeTab === 'members' && data?.seats && (
          <Badge>{t('members.seats', { used: data.seats.used, max: data.seats.max })}</Badge>
        )}
        {activeTab === 'members' && isAdmin && (
          <Button className="ml-auto" onClick={() => setInviteOpen((v) => !v)}>
            {t('members.invite')}
          </Button>
        )}
      </div>

      {activeTab === 'nutrients' && orgId ? (
        <OrgNutrientMappings orgId={orgId} canEdit={canEditNutrients} />
      ) : (
        <>
          {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
          {inviteOpen && (
            <Card title={t('members.inviteTitle')}>
              <p className="mb-3 text-sm text-slate-600">{t('members.inviteHelp')}</p>
              <div className="grid gap-3 md:grid-cols-[1fr_2fr_auto]">
                <Field label={t('members.email')}>
                  <Input
                    type="email"
                    value={invite.email}
                    onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                  />
                </Field>
                <Field label={t('members.rolesCol')}>
                  <RolePicker value={invite.roles} onChange={(roles) => setInvite({ ...invite, roles })} />
                </Field>
                <div className="flex items-end">
                  <Button
                    onClick={() => void sendInvite()}
                    disabled={!invite.email || invite.roles.length === 0}
                  >
                    {t('members.send')}
                  </Button>
                </div>
              </div>
            </Card>
          )}
          <Card>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">{t('members.email')}</th>
                  <th>{t('members.name')}</th>
                  <th>{t('members.status')}</th>
                  <th>{t('members.rolesCol')}</th>
                  {isAdmin && <th className="text-right">{t('members.actions')}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data?.members.map((m) => (
                  <tr key={m.email}>
                    <td className="py-2">
                      {m.email}{' '}
                      {m.email === data.adminEmail && <Badge tone="amber">{t('members.adminBadge')}</Badge>}
                    </td>
                    <td>{m.fullName ?? '-'}</td>
                    <td>
                      <Badge tone={statusTone(m.status)}>{t(`members.status_${m.status}`)}</Badge>
                    </td>
                    <td>
                      {editing?.uid === m.vaultUserId ? (
                        <RolePicker
                          value={editing.roles}
                          onChange={(roles) => setEditing({ uid: editing.uid, roles })}
                        />
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {m.roles.map((r) => (
                            <Badge key={r} tone="blue">
                              {t(`roles.${r}`)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="text-right">
                        {editing?.uid === m.vaultUserId ? (
                          <div className="flex justify-end gap-1">
                            <Button onClick={() => void saveRoles()} disabled={editing.roles.length === 0}>
                              {t('members.save')}
                            </Button>
                            <Button variant="secondary" onClick={() => setEditing(null)}>
                              {t('members.cancel')}
                            </Button>
                          </div>
                        ) : m.status === 'active' && m.vaultUserId ? (
                          <Button
                            variant="secondary"
                            onClick={() => setEditing({ uid: m.vaultUserId!, roles: m.roles })}
                          >
                            {t('members.rolesCol')}
                          </Button>
                        ) : m.status === 'invited' ? (
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="secondary"
                              disabled={!m.canResend}
                              onClick={() =>
                                api(`/api/v1/orgs/${orgId}/invites/${encodeURIComponent(m.email)}/resend`, {
                                  method: 'POST',
                                })
                                  .then(load)
                                  .catch(fail)
                              }
                            >
                              {t('members.resend')}
                            </Button>
                            <Button
                              variant="danger"
                              onClick={() =>
                                api(`/api/v1/orgs/${orgId}/invites/${encodeURIComponent(m.email)}`, {
                                  method: 'DELETE',
                                })
                                  .then(load)
                                  .catch(fail)
                              }
                            >
                              {t('members.cancelInvite')}
                            </Button>
                          </div>
                        ) : null}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function RolePicker({ value, onChange }: { value: Role[]; onChange: (r: Role[]) => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-2">
      {ALL_ROLES.map((r) => (
        <label key={r} className="inline-flex items-center gap-1 text-sm">
          <input
            type="checkbox"
            checked={value.includes(r)}
            onChange={() => onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r])}
          />
          {t(`roles.${r}`)}
        </label>
      ))}
    </div>
  );
}
